// commit-owner.ts
//
// The ONLY path that commits in autopilot (§5.4/§9.3): stage exactly the
// manifest files a ticket's attempt touched, run policy (fail-closed),
// journal `operation_intent` BEFORE running `git commit` (§3.4 — so a crash
// between intent and commit is reconcilable from git history: the
// `Autopilot-Run` trailer this commit carries is still there, even though
// R-25 removed the automatic trailer-search reconciler as unused), then
// journal `operation_result` with the base/head/tree SHA mapping receipt.
//
// `bootstrapCommit` is the SAME code path for the mission's very first
// commit (P5 init leaves files uncommitted on purpose) — ticket ref
// `T-0000`, conventional type `chore(autopilot)`. There is no other way to
// create the bootstrap commit than through this module (spec: "Không có
// đường commit nào khác ngoài commit-owner.ts").
import { randomUUID } from "node:crypto";
import {
  buildCommitMessage,
  commitStaged,
  type GitRunner,
  hasStagedChanges,
  resetIndex,
  revParse,
  stagePaths,
  tryRevParseHead,
} from "./commit-owner-git-ops.js";
import { formatSkippedPathspecsLine } from "./commit-owner-skipped-pathspecs-line.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";

export type { GitRunner } from "./commit-owner-git-ops.js";

export interface CommitOwnerBaseInput {
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  manifestPaths: string[];
  missionId: string;
  runId: string;
  /** Operator line for manifest pathspecs that staged nothing (review 260930 L5) — undefined ⇒ silent. */
  notify?: (line: string) => void;
}

export interface CommitTicketInput extends CommitOwnerBaseInput {
  ticketId: string;
  type: string;
  scope: string;
  subject: string;
}

export interface BootstrapCommitInput extends CommitOwnerBaseInput {}

export type CommitOwnerResult =
  | {
      status: "committed";
      baseSha: string | undefined;
      headSha: string;
      treeSha: string;
      commitMessage: string;
    }
  | { status: "no_change"; baseSha: string | undefined }
  | { status: "policy_denied"; reason: string };

const BOOTSTRAP_TICKET_REF = "T-0000";

async function runCommitFlow(
  input: CommitOwnerBaseInput,
  ticketRef: string,
  type: string,
  scope: string,
  subject: string,
  ticketIdForJournal: string | undefined,
): Promise<CommitOwnerResult> {
  const { store, repoDir, gitRunner, evaluatePolicy, manifestPaths, missionId, runId } = input;

  // `HEAD` may not exist yet for the mission's very first (bootstrap)
  // commit into a brand-new repo — tryRevParseHead never throws for that.
  const baseSha = await tryRevParseHead(gitRunner, repoDir);
  // Audit F3 (link 2): clear anything a worker's recipe staged behind our back
  // BEFORE staging the manifest, so the commit carries exactly `manifestPaths`.
  // Skipped when HEAD does not exist yet — the bootstrap commit into a fresh
  // repo has nothing to reset against (D5).
  if (baseSha !== undefined) {
    await resetIndex(gitRunner, repoDir);
  }
  const skipped = await stagePaths(gitRunner, repoDir, manifestPaths);
  if (skipped.length > 0) input.notify?.(formatSkippedPathspecsLine(ticketRef, skipped));

  const staged = await hasStagedChanges(gitRunner, repoDir);
  if (!staged) {
    const operationId = randomUUID();
    await store.commit([
      {
        event_id: `${operationId}-result`,
        operation_id: operationId,
        delta: {
          kind: "operation_result",
          operation_id: operationId,
          outcome: "no_change",
        },
      },
    ]);
    return { status: "no_change", baseSha };
  }

  const verdict = await evaluatePolicy({
    actor: "supervisor",
    operation: "git_commit",
    repo: repoDir,
    baseSha,
    policyDigest: "autopilot-commit-owner-v1",
    reviewStateRoot: repoDir,
    missionId,
  });
  if (verdict.decision !== "allow") {
    return { status: "policy_denied", reason: verdict.reason };
  }

  const commitMessage = buildCommitMessage({ type, scope, subject, ticketRef, missionId, runId });
  const operationId = randomUUID();

  // Intent BEFORE the actual `git commit` — crash after this point is
  // reconcilable from the `Autopilot-Run` trailer in git history (no
  // automatic scanner since R-25; look up by hand) instead of blindly
  // re-running `git commit` (never idempotent).
  await store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: "git_commit",
        params: { ticket_id: ticketIdForJournal ?? ticketRef, run_id: runId, base_sha: baseSha },
      },
    },
  ]);

  const headSha = await commitStaged(gitRunner, repoDir, commitMessage);
  const treeSha = await revParse(gitRunner, repoDir, "HEAD^{tree}");

  await store.commit([
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        outcome: "committed",
        remote_id: headSha,
      },
    },
  ]);

  return { status: "committed", baseSha, headSha, treeSha, commitMessage };
}

/** Commits exactly `manifestPaths` for one ticket's attempt. Never pushes. */
export async function commitTicket(input: CommitTicketInput): Promise<CommitOwnerResult> {
  return runCommitFlow(
    input,
    input.ticketId,
    input.type,
    input.scope,
    input.subject,
    input.ticketId,
  );
}

/**
 * The mission's first commit: files P5's `autopilot init` left uncommitted
 * on purpose (`.avatar/*`, ignore entries, ticket seed). Same intent →
 * policy → commit → receipt path as `commitTicket`, fixed ticket ref
 * `T-0000` and conventional type `chore(autopilot)`.
 */
export async function bootstrapCommit(input: BootstrapCommitInput): Promise<CommitOwnerResult> {
  return runCommitFlow(
    input,
    BOOTSTRAP_TICKET_REF,
    "chore",
    "autopilot",
    "bootstrap mission",
    undefined,
  );
}
